import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useEffect } from "react";
import SEO from "@/components/SEO";
import ArticleCard from "@/components/ArticleCard";
import FinalCTA from "@/components/home/FinalCTA";
import { ARTICLES, IMAGES, COMPANY } from "@/data/content";

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "article-schema";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.subtitle,
      articleSection: article.category,
      author: { "@type": "Organization", name: COMPANY.name },
      publisher: { "@type": "Organization", name: COMPANY.name },
    });
    document.head.appendChild(ld);
    return () => { document.getElementById("article-schema")?.remove(); };
  }, [article]);

  if (!article) return <Navigate to="/insights" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <>
      <SEO title={`${article.title} | All Data Tech Insights`} description={article.subtitle} path={`/insights/${slug}`} />

      <article>
        {/* Header */}
        <header className="bg-[#F7F8FA] pt-32 pb-10 lg:pt-40">
          <div className="ad-container max-w-3xl">
            <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1677FF] hover:text-[#00C7D9]">
              <ArrowLeft className="h-4 w-4" /> All Insights
            </Link>
            <div className="mt-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-wider">
              <span className="text-[#1677FF]">{article.category}</span>
              {article.isExample && (
                <span className="rounded-full border border-[#FF6A45]/30 bg-[#FF6A45]/10 px-2 py-0.5 text-[10px] text-[#FF6A45]">Example</span>
              )}
            </div>
            <h1 className="mt-4 display-lg text-[#111827]">{article.title}</h1>
            <p className="mt-5 text-lg leading-relaxed text-[#4B5563]">{article.subtitle}</p>
            <div className="mt-6 flex items-center gap-4 text-sm text-[#6B7280]">
              <span>{article.date}</span><span>·</span><span>{article.readTime}</span>
            </div>
          </div>
        </header>

        {/* Hero image */}
        <div className="bg-[#F7F8FA] pb-4">
          <div className="ad-container">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="img-overlay-blue relative overflow-hidden rounded-3xl border border-[#111827]/8">
              <img src={IMAGES[article.image]} alt={article.title} className="h-64 w-full object-cover sm:h-80 lg:h-[460px]" />
            </motion.div>
          </div>
        </div>

        {/* Body */}
        <div className="bg-white py-14 lg:py-20">
          <div className="ad-container max-w-3xl">
            {article.body.map((block, i) => {
              if (block.type === "h2")
                return <h2 key={i} className="mt-10 text-2xl font-extrabold text-[#111827] lg:text-3xl">{block.text}</h2>;
              if (block.type === "quote")
                return (
                  <blockquote key={i} className="my-10 border-l-4 border-[#1677FF] pl-6">
                    <p className="font-serif text-2xl leading-snug text-[#111827] lg:text-3xl">"{block.text}"</p>
                  </blockquote>
                );
              return <p key={i} className="mt-5 text-lg leading-relaxed text-[#374151]">{block.text}</p>;
            })}
          </div>
        </div>

        {/* Related */}
        <section className="bg-[#F7F8FA] py-16 lg:py-20">
          <div className="ad-container">
            <div className="flex items-end justify-between">
              <h2 className="text-2xl font-extrabold text-[#111827] lg:text-3xl">Related insights</h2>
              <Link to="/insights" className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#1677FF] hover:text-[#00C7D9]">
                View all <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="mt-8 grid gap-6 md:grid-cols-3">
              {related.map((a, i) => (
                <ArticleCard key={a.slug} article={a} index={i} testId={`related-article-${i}`} />
              ))}
            </div>
          </div>
        </section>
      </article>

      <FinalCTA />
    </>
  );
}
